// Mortgage-adjacent calculators: refinance, renewal, HELOC, land transfer tax, rent vs buy.
import { effectivePeriodicRate, amortizedPayment, round2 } from "./finance-math";
import { mortgagePayment } from "./mortgage";
import { LAND_TRANSFER, MARKET_RATES } from "../financial-data/canada";

function canadianMonthly(principal, annualRatePct, years) {
  const r = effectivePeriodicRate(Math.max(0, annualRatePct) / 100, 2, 12);
  return amortizedPayment({ principal, periodicRate: r, periods: years * 12 });
}

export function mortgageRefinance({ balance, currentRate, newRate, remainingYears, penalty = 0, closingCosts = 0 }) {
  const bal = Math.max(0, Number(balance) || 0);
  const yrs = Math.max(1, Number(remainingYears) || 1);
  const oldPmt = canadianMonthly(bal, Number(currentRate) || 0, yrs);
  const newBal = bal + (Number(penalty) || 0) + (Number(closingCosts) || 0);
  const newPmt = canadianMonthly(newBal, Number(newRate) || 0, yrs);
  const monthlySaving = oldPmt - newPmt;
  const upfront = (Number(penalty) || 0) + (Number(closingCosts) || 0);
  const breakeven = monthlySaving > 0 ? Math.ceil(upfront / monthlySaving) : null;
  const oldInterest = oldPmt * yrs * 12 - bal;
  const newInterest = newPmt * yrs * 12 - newBal;
  return {
    newPayment: round2(newPmt), oldPayment: round2(oldPmt),
    monthlySaving: round2(monthlySaving), upfrontCost: round2(upfront),
    breakevenMonths: breakeven,
    lifetimeSaving: round2((oldInterest) - (newInterest) - upfront),
    worthIt: monthlySaving > 0 && breakeven !== null && breakeven <= yrs * 12,
  };
}

export function mortgageRenewal({ balance, oldRate, newRate, remainingYears, frequency = "monthly" }) {
  const bal = Math.max(0, Number(balance) || 0);
  const yrs = Math.max(1, Number(remainingYears) || 1);
  const oldR = mortgagePayment({ principal: bal, annualRate: Number(oldRate) || 0, amortizationYears: yrs, frequency });
  const newR = mortgagePayment({ principal: bal, annualRate: Number(newRate) || 0, amortizationYears: yrs, frequency });
  return {
    newPayment: round2(newR.payment), oldPayment: round2(oldR.payment),
    paymentChange: round2(newR.payment - oldR.payment),
    newTotalInterest: round2(newR.totalInterest), oldTotalInterest: round2(oldR.totalInterest),
    interestChange: round2(newR.totalInterest - oldR.totalInterest),
    frequencyLabel: newR.frequencyLabel,
  };
}

export function heloc({ homeValue, mortgageBalance = 0, annualRate, drawAmount = 0 }) {
  const hv = Math.max(0, Number(homeValue) || 0);
  const mb = Math.max(0, Number(mortgageBalance) || 0);
  const maxRevolving = hv * 0.65;                 // HELOC portion capped at 65% of value
  const maxCombined = hv * 0.80 - mb;             // total secured lending capped at 80%
  const available = Math.max(0, Math.min(maxRevolving, maxCombined));
  const rate = Math.max(0, Number(annualRate) || 0) / 100 / 12;
  const draw = Math.min(Math.max(0, Number(drawAmount) || 0), available);
  return {
    availableCredit: round2(available),
    interestOnlyMonthly: round2(draw * rate),
    drawAmount: round2(draw),
    maxAt65: round2(maxRevolving),
    maxCombined80: round2(Math.max(0, maxCombined)),
  };
}

export function landTransferTax({ price, province = "ON", firstTimeBuyer = false, toronto = false }) {
  const p = Math.max(0, Number(price) || 0);
  const rules = LAND_TRANSFER[province] || LAND_TRANSFER.GENERIC;
  const applyBrackets = (brackets) => {
    let tax = 0, lower = 0;
    for (const b of brackets) {
      if (p > lower) { tax += (Math.min(p, b.upTo) - lower) * b.rate; lower = b.upTo; } else break;
    }
    return tax;
  };
  let provincial = 0, municipal = 0, rebate = 0, estimate = false;
  if (rules.brackets) {
    provincial = applyBrackets(rules.brackets);
    if (province === "ON" && toronto) municipal = applyBrackets(rules.brackets);
    if (firstTimeBuyer) {
      if (province === "ON") {
        rebate += Math.min(provincial, rules.ftbRebate || 0);
        if (toronto) rebate += Math.min(municipal, rules.torontoFtbRebate || 0);
      } else if (province === "BC" && p <= (rules.ftbExemptionUpTo || 0)) {
        rebate += provincial; // full exemption under threshold
      }
    }
  } else {
    estimate = true;
    provincial = p * (rules.flatRate || 0.01);
  }
  const total = Math.max(0, provincial + municipal - rebate);
  return {
    total: round2(total), provincial: round2(provincial), municipal: round2(municipal),
    rebate: round2(rebate), estimate, provinceName: rules.name,
  };
}

// Rent vs Buy over N years: compares net worth outcome of buying vs renting+investing the difference.
export function rentVsBuy({
  homePrice, downPayment, mortgageRate, amortizationYears = 25, years = 5,
  propertyTaxRate = 1.0, maintenanceRate = 1.0, homeAppreciation = 3.0, closingCostPct = 1.5,
  monthlyRent, rentIncrease = 3.0, investmentReturn = 5.0,
}) {
  const price = Math.max(0, Number(homePrice) || 0);
  const down = Math.max(0, Number(downPayment) || 0);
  const N = Math.max(1, Number(years) || 1);
  const mortgage = Math.max(0, price - down);
  const mPay = canadianMonthly(mortgage, Number(mortgageRate) || 0, amortizationYears);
  const mRate = effectivePeriodicRate((Number(mortgageRate) || 0) / 100, 2, 12);

  // Buying path
  let bal = mortgage;
  let buyMonthlyCosts = 0;
  const monthlyTax = (price * (propertyTaxRate / 100)) / 12;
  const monthlyMaint = (price * (maintenanceRate / 100)) / 12;
  for (let m = 0; m < N * 12; m++) {
    const interest = bal * mRate;
    bal = Math.max(0, bal - (mPay - interest));
  }
  const homeValue = price * Math.pow(1 + homeAppreciation / 100, N);
  const sellingCost = homeValue * 0.05; // realtor + fees on exit
  const buyEquity = homeValue - bal - sellingCost;
  const closing = price * (closingCostPct / 100);
  buyMonthlyCosts = mPay + monthlyTax + monthlyMaint;

  // Renting path: invest (down + closing) and any monthly savings vs buying
  let invest = down + closing;
  let rent = Math.max(0, Number(monthlyRent) || 0);
  const invRate = Math.pow(1 + investmentReturn / 100, 1 / 12) - 1;
  for (let y = 0; y < N; y++) {
    for (let m = 0; m < 12; m++) {
      const diff = buyMonthlyCosts - rent; // if buying costs more, renter invests the difference
      invest = invest * (1 + invRate) + Math.max(0, diff);
    }
    rent = rent * (1 + rentIncrease / 100);
  }
  const rentWealth = invest;
  const difference = buyEquity - rentWealth;
  return {
    buyNetWorth: round2(buyEquity),
    rentNetWorth: round2(rentWealth),
    difference: round2(difference),
    winner: difference >= 0 ? "buy" : "rent",
    monthlyOwnershipCost: round2(buyMonthlyCosts),
    monthlyRentStart: round2(Number(monthlyRent) || 0),
    homeValueEnd: round2(homeValue),
    mortgageBalanceEnd: round2(bal),
  };
}

export { MARKET_RATES };
